/**
 * Notifications Store — Product Management Portal
 *
 * Manages pending product request notifications state.
 * Fetches pending requests on login/refresh and exposes them for the
 * notification bell dropdown.
 */
import { defineStore } from 'pinia';
import { computeDiff } from '@tms-phoenix/ui-vue';
import type { ProductRequestListItem, ProductRequestDynamoItem } from '@tms-phoenix/tms-types';
import {
  fetchPendingRequests,
  fetchPendingCount,
  fetchProductRequestDetail,
  actionProductRequest,
} from '@/modules/products/services/product-requests.api';
import { getApiErrorMessage } from '@/core/utils/api-error.util';
import type { ActionProductRequest } from '@tms-phoenix/tms-types';
import { logAuditEvent } from '@/core/services';

const computeAuditDiff = computeDiff;

interface NotificationsState {
  pendingRequests: ProductRequestListItem[];
  pendingCount: number;
  selectedRequest: ProductRequestDynamoItem | null;
  isLoadingList: boolean;
  isLoadingDetail: boolean;
  isSubmitting: boolean;
  error: string | null;
  showPanel: boolean;
  showDetail: boolean;
}

export const useNotificationsStore = defineStore('notifications', {
  state: (): NotificationsState => ({
    pendingRequests: [],
    pendingCount: 0,
    selectedRequest: null,
    isLoadingList: false,
    isLoadingDetail: false,
    isSubmitting: false,
    error: null,
    showPanel: false,
    showDetail: false,
  }),

  actions: {
    /**
     * Fetch pending product requests and the total count for the badge.
     */
    async loadPendingRequests() {
      this.isLoadingList = true;
      this.error = null;
      try {
        const listResponse = await fetchPendingRequests({ limit: 50 });
        const items = Array.isArray(listResponse.data) ? listResponse.data : [];
        this.pendingRequests = items;
        // Best-effort badge count — don't block the list if this fails
        try {
          const countResponse = await fetchPendingCount();
          this.pendingCount = countResponse.data?.count ?? items.length;
        } catch {
          this.pendingCount = items.length;
        }
      } catch (err: unknown) {
        this.error = getApiErrorMessage(err, 'Failed to load notifications');
      } finally {
        this.isLoadingList = false;
      }
    },

    /**
     * Fetch details for a single product request.
     */
    async loadRequestDetail(requestId: string) {
      this.selectedRequest = null;
      this.showDetail = true;
      this.isLoadingDetail = true;
      this.error = null;
      try {
        const response = await fetchProductRequestDetail(requestId);
        this.selectedRequest = response.data;
      } catch (err: unknown) {
        this.error = getApiErrorMessage(err, 'Failed to load request details');
        this.showDetail = false;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    /**
     * Approve or reject a product request.
     */
    async submitAction(requestId: string, payload: ActionProductRequest) {
      this.isSubmitting = true;
      this.error = null;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      const auditEventType = payload.action === 'approve' ? 'APPROVE' : 'REJECT';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      const currentItem = this.selectedRequest
        ?? this.pendingRequests.find((r) => r.request_id === requestId)
        ?? null;
      const existingSnapshot: Record<string, unknown> = {
        status: currentItem?.status,
        review_notes: (currentItem as { review_notes?: string | null } | null)?.review_notes ?? null,
        approved_product_name: (currentItem as { approved_product_name?: string | null } | null)?.approved_product_name ?? null,
        rejection_reason: (currentItem as { rejection_reason?: string | null } | null)?.rejection_reason ?? null,
        rejection_detail: (currentItem as { rejection_detail?: string | null } | null)?.rejection_detail ?? null,
      };
      const updatedSnapshot: Record<string, unknown> = payload.action === 'approve'
        ? {
            status: 'APPROVED',
            review_notes: payload.review_notes,
            approved_product_name: payload.approved_product_name,
          }
        : {
            status: 'REJECTED',
            review_notes: payload.review_notes,
            rejection_reason: payload.rejection_reason,
            rejection_detail: payload.rejection_detail,
          };
      const diff = computeAuditDiff(existingSnapshot, updatedSnapshot);

      try {
        await actionProductRequest(requestId, payload);
        auditStatus = 'SUCCESS';
        // Remove from pending list
        this.pendingRequests = this.pendingRequests.filter(
          (r) => r.request_id !== requestId,
        );
        this.pendingCount = Math.max(0, this.pendingCount - 1);
        this.showDetail = false;
        this.selectedRequest = null;
        return true;
      } catch (err: unknown) {
        this.error = getApiErrorMessage(err, 'Action failed');
        auditStatus = 'FAILURE';
        auditErrorCode = payload.action === 'approve'
          ? 'APPROVE_PRODUCT_REQUEST_FAILED'
          : 'REJECT_PRODUCT_REQUEST_FAILED';
        auditErrorMessage = this.error;
        return false;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'NOTIFICATIONS',
            eventType: auditEventType,
            eventStatus: auditStatus,
            resourceType: 'productRequests',
            resourceId: requestId,
            oldValue: diff.oldValue,
            newValue: diff.newValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            isPhiAccess: false,
          });
        } catch {
          // Audit must never block the flow
        }
        this.isSubmitting = false;
      }
    },

    togglePanel() {
      this.showPanel = !this.showPanel;
    },

    closePanel() {
      this.showPanel = false;
    },

    closeDetail() {
      this.showDetail = false;
      this.selectedRequest = null;
    },
  },
});
